import React, { useCallback, useMemo, useRef, useState, useEffect } from "react"
import { Container, Grid, Chip, Card, CardContent, Typography, Box } from "@material-ui/core"
import { useHistory } from "react-router";
import { useMutation } from "react-query";
import { fetchAllVideos } from "../services"
import TableContainerServerSide from "../components/TableContainerServerSide"
import { Button } from "../components/button"
import DefaultFilter from "../components/DefaultFilter"
import SelectColumnFilter from "../components/SelectFilter"
import DateRangeFilter from "../components/DateRangeFilter"
import moment from 'moment';
import { useAuth } from "../utils/Auth";

function Videos() {
    const { user } = useAuth()
    const history = useHistory()
    const [total, setTotal] = useState(0)
    const [totalAssigned, setTotalAssigned] = useState(0)
    const [totalUnassigned, setTotalUnassigned] = useState(0)
    const [totalEmptyPaid, setTotalEmptyPaid] = useState(0)
    const [totalFilteredAssigned, setTotalFilteredAssigned] = useState(0)
    const [totalFilteredUnassigned, setTotalFilteredUnassigned] = useState(0)
    const [totalFilteredEmptyPaid, setTotalFilteredEmptyPaid] = useState(0)
    const [pageCount, setPageCount] = useState(1)
    const [videos, setVideos] = useState([])
    const [dateRange, setDateRange] = useState(null)
    const request = useRef()

    const columns = useMemo(() => [
        {
            Header: 'Video UID',
            id: 'video_uid',
            accessor: 'video_uid',
            Filter: DefaultFilter,
        },
        {
            Header: 'Project Name',
            id: 'project_name',
            accessor: 'project_name',
            Filter: DefaultFilter,
        },
        {
            Header: 'Company Name',
            id: 'company_name',
            accessor: 'company_name',
            Filter: DefaultFilter,
        },
        {
            Header: 'Status',
            id: 'project_status',
            accessor: 'project_status',
            Filter: SelectColumnFilter,
            FilterOptions: ['Finished', 'Ongoing', 'Pending offer', 'Cancelled'],
            Cell: ({ row }) => {
                const { project_status, status_simplified } = row.original;
                let chipColor = ''

                switch (status_simplified) {
                    case 'finished':
                        chipColor = 'green'
                        break;
                    case 'ongoing':
                        chipColor = 'blue'
                        break;
                    case 'pending offer':
                        chipColor = 'grey'
                        break;
                    case 'cancelled':
                        chipColor = 'red'
                        break;
                    default:
                        chipColor = 'grey'
                }

                return <Chip size="small" className={`chip-${chipColor}`} label={project_status} />
            }
        },
        {
            Header: 'Timeline',
            id: 'timeline',
            accessor: 'timeline_label',
            Filter: SelectColumnFilter,
            FilterOptions: ['No brief', 'No creator', 'Delivery', 'Scripting', 'Filming', 'Review', 'Reshooting', 'Approved'],
            Cell: ({ row }) => {
                const { timeline_label } = row.original;
                let chipColor = ''

                switch (timeline_label) {
                    case 'Approved':
                        chipColor = 'green'
                        break;
                    case 'Filming':
                    case 'Review':
                        chipColor = 'blue'
                        break;
                    case 'Reshooting':
                        chipColor = 'orange'
                        break;
                    case 'Scripting':
                        chipColor = 'grey'
                        break;
                    case 'Delivery':
                        chipColor = 'purple'
                        break;
                    case 'No brief':
                        chipColor = 'red'
                        break;
                    case 'No creator':
                        chipColor = 'red'
                        break;
                    default:
                        chipColor = 'grey'
                }

                return <Chip size="small" className={`chip-${chipColor}`} label={timeline_label} />
            }
        },
        {
            Header: 'Date Start',
            id: 'date_submit',
            accessor: ({ date_submit }) => {
                return date_submit ? moment(date_submit).format('LL') : 'N/A'
            },
            sortType: (rowA, rowB) => {
                const dateA = rowA.original.date_submit
                const dateB = rowB.original.date_submit

                if (!dateA && !dateB) return 0
                if (!dateA) return 1
                if (!dateB) return -1

                const timeA = new Date(dateA).getTime()
                const timeB = new Date(dateB).getTime()

                return timeA - timeB
            },
            isSortable: true,
            disableFilters: true,
        },
        {
            Header: 'Date Finish',
            id: 'date_finish',
            accessor: ({ date_finish }) => {
                return date_finish ? moment(date_finish).format('LL') : 'N/A'
            },
            sortType: (rowA, rowB) => {
                const dateA = rowA.original.date_finish
                const dateB = rowB.original.date_finish

                if (!dateA && !dateB) return 0
                if (!dateA) return 1
                if (!dateB) return -1

                const timeA = new Date(dateA).getTime()
                const timeB = new Date(dateB).getTime()

                return timeA - timeB
            },
            isSortable: true,
            disableFilters: true,
        },
        {
            Header: 'Video Type',
            id: 'video_type',
            accessor: 'video_type',
            disableFilters: true
        },
        {
            Header: 'Creator Phone',
            id: 'creator_phone',
            accessor: ({ creator_phone, creator_name }) => {
                return creator_phone && creator_name ? `${creator_phone} (${creator_name})` : creator_phone || 'Not assigned'
            },
            disableFilters: true
        },
        {
            Header: 'Brief filled',
            id: 'is_submitted',
            accessor: ({ is_submitted }) => is_submitted ? 'Yes' : 'No',
            Filter: SelectColumnFilter,
            FilterOptions: ['Yes', 'No'],
            filterValue: 'Yes',
            Cell: ({ row }) => {
                const { is_submitted } = row.original;
                return <Chip size="small" className={`chip-${is_submitted ? 'green' : 'red'}`} label={is_submitted ? 'Yes' : 'No'} />
            }
        },
        {
            Header: 'Matchmaking done',
            id: 'video_assigned',
            accessor: ({ video_assigned }) => video_assigned ? 'Yes' : 'No',
            Filter: SelectColumnFilter,
            FilterOptions: ['Yes', 'No'],
            filterValue: 'No',
            Cell: ({ row }) => {
                const { video_assigned } = row.original;
                return <Chip size="small" className={`chip-${video_assigned === "Yes" ? 'green' : 'red'}`} label={video_assigned === "Yes" ? 'Yes' : 'No'} />
            }
        },
        {
            Header: '#days unassigned',
            id: 'days_since_submit',
            accessor: ({ date_submit, video_assigned }) => {
                if (!date_submit || video_assigned === "Yes") return 'N/A'
                const daysDiff = moment().diff(moment(date_submit), 'days')
                return daysDiff
            },
            disableFilters: true,
            Cell: ({ row }) => {
                const { date_submit, video_assigned } = row.original;
                if (!date_submit || video_assigned === "Yes") return <span>N/A</span>

                const daysDiff = moment().diff(moment(date_submit), 'days')
                let chipColor = 'grey'

                if (daysDiff <= 3) {
                    chipColor = 'green'
                } else if (daysDiff <= 7) {
                    chipColor = 'blue'
                } else if (daysDiff <= 30) {
                    chipColor = 'orange'
                } else {
                    chipColor = 'red'
                }

                return <Chip size="small" className={`chip-${chipColor}`} label={`${daysDiff} days`} />
            }
        },
        {
            Header: "Actions",
            Filter: ``,
            Cell: ({ cell }) => {
                let { video_id, project_id, video_uid, project_uid } = cell.row.original
                return (
                    <div className="is-flex aic">
                        <a className="stllr-btn btn-sm secondary bordered is-flex aic jcc mr-10" style={{ padding: "0px 8px" }} target="_blank" href={`${process.env.REACT_APP_STLLR_URL}en/videos/${video_uid}`}>VIEW VIDEO</a>
                        <a className="stllr-btn btn-sm secondary bordered is-flex aic jcc mr-10" style={{ padding: "0px 8px" }} href={`/edit-project/${project_id}/ugc`}>VIEW PROJECT</a>
                        {/* <a className="stllr-btn btn-sm secondary bordered is-flex aic jcc" style={{ padding: "0px 8px" }} href={`/project-history/${project_id}`}>PROJECT HISTORY</a> */}
                    </div>
                )
            }
        }
    ], [])

    const { mutate: fetch, isLoading } = useMutation(params => fetchAllVideos(null, params), {
        onSuccess: ({ videos, total: responseTotal, pageCount: responsePageCount, total_assigned, total_unassigned, total_empty_paid, filtered_total_assigned, filtered_total_unassigned, filtered_total_empty_paid }) => {
            setVideos(videos?.data || videos)
            setTotal(responseTotal || videos?.length || 0)
            setTotalAssigned(total_assigned || 0)
            setTotalUnassigned(total_unassigned || 0)
            setTotalEmptyPaid(total_empty_paid || 0)
            setTotalFilteredAssigned(filtered_total_assigned || 0)
            setTotalFilteredUnassigned(filtered_total_unassigned || 0)
            setTotalFilteredEmptyPaid(filtered_total_empty_paid || 0)
            if (responsePageCount !== pageCount) {
                setPageCount(responsePageCount || 1)
            }
        }
    })

    const handleRefetchData = useCallback(
        ({ pageIndex, pageSize, search, export: exportColumns }) => {
            const searchParams = { ...search };

            // Add date range to search parameters if set
            if (dateRange) {
                searchParams.date_range = { startDate: dateRange.startDate, endDate: dateRange.endDate }
            }

            fetch({
                pageIndex,
                pageSize,
                search: searchParams,
                export: exportColumns
            })
        },
        [dateRange]
    )

    const handleDateRangeChange = (range) => {
        setDateRange(range);
    };

    const handleClearDateRange = () => {
        setDateRange(null);
    };

    // Trigger fetch when date range changes
    useEffect(() => {
        handleRefetchData({
            pageIndex: 0,
            pageSize: 10,
            search: {}
        });
    }, [dateRange, handleRefetchData]);

    return (
        <Container spacing={1} maxWidth='xl'>
            <Grid item lg={12}>
                <div className="is-flex aic jcsb">
                    <h2 className="mt-0 mb-10 mt-20">Videos (<i>{total}</i>)</h2>
                </div>

                {/* Stats Cards */}
                <Box display="flex" mb={3} style={{ gap: '12px' }}>
                    <Card style={{ minWidth: 160, backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
                        <CardContent style={{ padding: '12px', textAlign: 'center' }}>
                            <div className="is-flex aic jcc" style={{ gap: '8px' }}>
                                <Typography variant="caption" color="textSecondary" style={{ fontWeight: '400' }}>
                                    Total
                                </Typography>
                                <Typography variant="h6" component="div" style={{ color: '#6c757d', fontWeight: '500', marginBottom: '4px' }}>
                                    {totalAssigned}
                                </Typography>
                            </div>
                            <div className="is-flex aic jcc" style={{ gap: '8px' }}>
                                <Typography variant="caption" color="textSecondary" style={{ fontWeight: '400' }}>
                                    Filtered
                                </Typography>
                                <Typography variant="h6" component="div" style={{ color: '#6c757d', fontWeight: '500', marginBottom: '4px' }}>
                                    {totalFilteredAssigned}
                                </Typography>
                            </div>
                            <Typography variant="caption" color="textSecondary" style={{ fontWeight: '400' }}>
                                Assigned Videos
                            </Typography>
                        </CardContent>
                    </Card>

                    <Card style={{ minWidth: 160, backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
                        <CardContent style={{ padding: '12px', textAlign: 'center' }}>
                            <div className="is-flex aic jcc" style={{ gap: '8px' }}>
                                <Typography variant="caption" color="textSecondary" style={{ fontWeight: '400' }}>
                                    Total
                                </Typography>
                                <Typography variant="h6" component="div" style={{ color: '#6c757d', fontWeight: '500', marginBottom: '4px' }}>
                                    {totalUnassigned}
                                </Typography>
                            </div>
                            <div className="is-flex aic jcc" style={{ gap: '8px' }}>
                                <Typography variant="caption" color="textSecondary" style={{ fontWeight: '400' }}>
                                    Filtered
                                </Typography>
                                <Typography variant="h6" component="div" style={{ color: '#6c757d', fontWeight: '500', marginBottom: '4px' }}>
                                    {totalFilteredUnassigned}
                                </Typography>
                            </div>
                            <Typography variant="caption" color="textSecondary" style={{ fontWeight: '400' }}>
                                Unassigned Videos
                            </Typography>
                        </CardContent>
                    </Card>

                    <Card style={{ minWidth: 160, backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
                        <CardContent style={{ padding: '12px', textAlign: 'center' }}>
                            <div className="is-flex aic jcc" style={{ gap: '8px' }}>
                                <Typography variant="caption" color="textSecondary" style={{ fontWeight: '400' }}>
                                    Total
                                </Typography>
                                <Typography variant="h6" component="div" style={{ color: '#6c757d', fontWeight: '500', marginBottom: '4px' }}>
                                    {totalEmptyPaid}
                                </Typography>
                            </div>
                            <div className="is-flex aic jcc" style={{ gap: '8px' }}>
                                <Typography variant="caption" color="textSecondary" style={{ fontWeight: '400' }}>
                                    Filtered
                                </Typography>
                                <Typography variant="h6" component="div" style={{ color: '#6c757d', fontWeight: '500', marginBottom: '4px' }}>
                                    {totalFilteredEmptyPaid}
                                </Typography>
                            </div>

                            <Typography variant="caption" color="textSecondary" style={{ fontWeight: '400' }}>
                                Unfilled brief paid videos
                            </Typography>
                        </CardContent>
                    </Card>
                </Box>

                {/* Date Range Filter */}
                <DateRangeFilter
                    onDateRangeChange={handleDateRangeChange}
                    onClear={handleClearDateRange}
                />

                <TableContainerServerSide
                    name='videos'
                    paging={true}
                    columns={columns}
                    hiddenColumns={[]}
                    data={videos}
                    hasFilters={true}
                    allowSorting={true}
                    loading={isLoading}
                    pageCount={pageCount}
                    fetchData={handleRefetchData}
                    dataFetcher={(params) => fetchAllVideos(null, params)}
                />
            </Grid>
        </Container>
    )
}

export default Videos 